import { Outlet, useNavigate } from "react-router-dom";
import {
  Box, Drawer, IconButton, List, SpeedDial, Stack, Toolbar, useMediaQuery,
  ListItemButton, ListItemIcon, Typography
} from "@mui/material";
import sizeConfigs from "../../configs/sizeConfigs";
import colorConfigs from "../../configs/colorConfigs";
import React, { useState } from "react";
import Sidebar from "../Sidebar";
import ProfileButton from "../common/buttons/ProfileButton";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import assets from "../../assets";
import appRoutes from "../../routes/appRoutes";
import SidebarItemCollapse from "../SidebarItemCollapse";
import SidebarItem from "../SidebarItem";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import SignInDialog from "../common/SignInDialog";
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { logout } from "../../redux/features/auth/authSlice";
import EditProfileCard from "../common/cards/EditProfileCard";
import RecordDetailDialog from "../common/RecordDetailDialog";

const MainLayout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const authData = useSelector((state: RootState) => state.auth);
  const [isHovered, setIsHovered] = useState(false);
  const [popup, setPopup] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleBackButton = () => {
    setPopup("");
  };
  const isMobile = useMediaQuery('(max-width:768px)');

  const mobiledrawer = (
    <Box data-testid="Box-411f"
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 1,
      }}
    >
      <IconButton data-testid="IconButton-j9gl"
        edge="start"
        color="inherit"
        aria-label="close"
        onClick={handleDrawerToggle}
        sx={{ position: 'absolute', top: 16, right: 16 }}
      >
        <CloseIcon sx={{ color: 'primary.main' }} />
      </IconButton>
      <List
        onClick={handleDrawerToggle}
        sx={{
          width: '90%',
          height: "80%",
          p: 0,
          m: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}
      >
        <Toolbar disableGutters sx={{ height: "100px", transition: "all 0.3s ease" }}>
          <Stack data-testid="Stack-67c5"
            sx={{ width: "100%", height: "80px" }}
            direction="row"
            justifyContent="center"
          >
            <Box data-testid="Box-l0ai"
              component="img"
              sx={{
                height: "auto",
                transition: "all 0.3s ease",
                objectFit: "contain",
              }}
              alt="Premier Logo"
              src={assets.images.premierbiotechlogo}
            />
          </Stack>
        </Toolbar>
        {appRoutes.map((route: any, index: any) => {
          if (authData !== undefined && !authData.isAuthenticated)
            return <SignInDialog isAuthenticated={authData.isAuthenticated} key={index} />;
          if (route.sidebarProps && authData !== undefined) {
            if (route.child) {
              return <SidebarItemCollapse item={route} key={index} isHovered={isHovered} />;
            } else {
              return <SidebarItem item={route} key={index} isHovered={isHovered} />;
            }
          }
          return null;
        })}
        <Box data-testid="Box-ysbd"
          sx={{
            mt: 'auto',
            display: 'flex',
            flexDirection: 'column',
            pt: 1,
            gap: 1,
          }}
        >
          <ListItemButton data-testid="ListItemButton-xvnf"
            disableRipple
            onClick={() => {
              handleDrawerToggle();
              setPopup('Edit');
            }}
            sx={{
              "&:hover": {
                color: colorConfigs.sidebar.activeBg,
                cursor: 'pointer',
                backgroundColor: colorConfigs.sidebar.bg,
                // Apply hover effect to the icon inside ListItemButton
                "& .MuiListItemIcon-root": {
                  color: colorConfigs.sidebar.activeBg,
                },
              },
              "&.Mui-selected": {
                backgroundColor: colorConfigs.sidebar.bg,
                color: colorConfigs.sidebar.activeBg,
                "&:hover": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
                "&:focus": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
                "&:active": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
              },
              "&.Mui-selected.Mui-focusVisible": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected:active": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected:focus": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              color: colorConfigs.sidebar.color,
              paddingY: 0.2,
              opacity: 1,
              position: 'relative', // Required for Badge
            }}
          >
            <ListItemIcon data-testid="ListItemIcon-tutw" sx={{
              minWidth: "unset",
              marginLeft: "7px",
              p: 0,
              marginRight: "20px",
              justifyContent: "center",
              transition: "margin-right 0.3s",
              color: colorConfigs.sidebar.color,  // Keep the displayText color as is
              "&:hover": {
                color: colorConfigs.sidebar.activeBg,
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected": {
                backgroundColor: colorConfigs.sidebar.bg,
                color: colorConfigs.sidebar.activeBg,
                "&:hover": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
                "&:focus": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
                "&:active": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
              },
              "&.Mui-selected.Mui-focusVisible": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected:active": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected:focus": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
            }}>
              <AccountBoxIcon fontSize="large" sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            {isMobile && (
              <Typography data-testid="Typography-j7pk" variant="body1">Profile</Typography>
            )}
          </ListItemButton>
          <ListItemButton data-testid="ListItemButton-8m53"
            disableRipple
            onClick={() => {
              handleDrawerToggle();
              dispatch(logout());
              navigate('/login');
            }}
            sx={{
              "&:hover": {
                color: colorConfigs.sidebar.activeBg,
                cursor: 'pointer',
                backgroundColor: colorConfigs.sidebar.bg,
                // Apply hover effect to the icon inside ListItemButton
                "& .MuiListItemIcon-root": {
                  color: colorConfigs.sidebar.activeBg,
                },
              },
              "&.Mui-selected": {
                backgroundColor: colorConfigs.sidebar.bg,
                color: colorConfigs.sidebar.activeBg,
                "&:hover": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
                "&:focus": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
                "&:active": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
              },
              "&.Mui-selected.Mui-focusVisible": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected:active": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected:focus": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              color: colorConfigs.sidebar.color,
              paddingY: 0.2,
              opacity: 1,
              position: 'relative', // Required for Badge
            }}
          >
            <ListItemIcon data-testid="ListItemIcon-g5uk" sx={{
              ml: 0.5,
              minWidth: "unset",
              marginLeft: "7px",
              p: 0,
              marginRight: "20px",
              justifyContent: "center",
              transition: "margin-right 0.3s",
              color: colorConfigs.sidebar.color,  // Keep the displayText color as is
              "&:hover": {
                color: colorConfigs.sidebar.activeBg,
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected": {
                backgroundColor: colorConfigs.sidebar.bg,
                color: colorConfigs.sidebar.activeBg,
                "&:hover": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
                "&:focus": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
                "&:active": {
                  backgroundColor: colorConfigs.sidebar.bg,
                },
              },
              "&.Mui-selected.Mui-focusVisible": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected:active": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
              "&.Mui-selected:focus": {
                backgroundColor: colorConfigs.sidebar.bg,
              },
            }}>
              <LogoutIcon fontSize="large" sx={{ color: colorConfigs.buttons.cancel }} />
            </ListItemIcon>
            {isMobile && (
              <Typography data-testid="Typography-pl0t" variant="body1" sx={{ color: colorConfigs.buttons.cancel }}>Logout</Typography>
            )}
          </ListItemButton>
        </Box>
      </List>
    </Box>
  );

  return (
    <Box data-testid="Box-16ct" sx={{ display: "flex", m: 0 }}>
      {/* Mobile layout */}
      <Box data-testid="Box-zzm5" display={{ xs: "block", sm: "block", md: 'none' }}>
        <SpeedDial data-testid="SpeedDial-tkub"
          ariaLabel="Nav Menu"
          color="inherit"
          aria-label="menu"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 11,
            '& .MuiFab-primary': {
              width: '40px',
              height: '40px',
              boxShadow: 1,
              borderRadius: 6,
            },
            '& .MuiSpeedDial-actions': {
              '& .MuiSpeedDialAction-fab': {}
            }
          }}
          icon={<MenuIcon />}
        />
        <Drawer
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          PaperProps={{
            sx: {
              width: '100vw',
              height: '100vh',
              background: 'rgba(255, 255, 255, 0.92)'
            },
          }}
        >
          {mobiledrawer}
        </Drawer>
      </Box>
      {/* Desktop sidebar */}
      <Box data-testid="Box-ii6v" display={{ xs: 'none', sm: 'none', md: 'block' }}>
        <ProfileButton />
      </Box>
      <Box data-testid="Box-gjtz"
        component="nav"
        sx={{
          display: { xs: 'none', sm: 'none', md: 'block' },
          width: sizeConfigs.sidebar.halfwidth,
          flexShrink: 0,
          overflowY: "auto",
        }}
      >
        <Sidebar />
      </Box>
      <Box data-testid="Box-swto"
        component="main"
        sx={{
          flexGrow: 1,
          paddingX: 0,
          width: `calc(100vw - ${sizeConfigs.sidebar.halfwidth})`,
          height: "100vh",
          backgroundColor: colorConfigs.topbar.bg2,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          overflowX: "hidden"
        }}
      >
        <Outlet />
      </Box>
      <RecordDetailDialog open={popup === "Edit"} title="Edit Profile" onClose={handleBackButton} compactHeader>
        <EditProfileCard setPopup={setPopup} handleBackButton={handleBackButton} />
      </RecordDetailDialog>
    </Box>
  );
};

export default MainLayout;
